import type { Lang } from "./i18n";

export const siteOrigin = "https://clawease.ai";

export type SeoPage =
  | "home"
  | "contact"
  | "privacy"
  | "cookie"
  | "terms"
  | "about"
  | "download"
  | "demo"
  | "ws-inspector"
  | "not-found";

type RobotsValue = "index,follow" | "noindex,follow" | "noindex,nofollow";

export interface SeoDocument {
  title: string;
  description: string;
  keywords: string[];
  robots: RobotsValue;
  canonicalUrl: string | null;
  alternates: Array<{ hreflang: string; href: string }>;
  structuredData: Record<string, unknown> | null;
}

const seoCopy: Record<SeoPage, Record<Lang, { title: string; description: string }>> = {
  home: {
    en: {
      title: "ClawEase | Free AI Receptionist for Every Industry",
      description:
        "ClawEase is a multilingual AI receptionist. Handle Cantonese, Mandarin and English across WhatsApp, phone and email, and connect your calendar, PMS and CRM workflows.",
    },
    zh: {
      title: "ClawEase | 面向各行各业的免费 AI 接待员",
      description:
        "ClawEase 多语言 AI 接待员支持广东话、普通话和英语，整合 WhatsApp、电话及邮件，接通日历、PMS 和 CRM，全天候接待并跟进客户需求。",
    },
  },
  contact: {
    en: {
      title: "ClawEase | Contact | Book a Demo",
      description: "Book a free ClawEase demo for AI customer service, an AI receptionist, and automated business workflows tailored to your service team.",
    },
    zh: {
      title: "ClawEase | 联系我们 | 预约产品演示",
      description: "联系 ClawEase，确认你的业务场景并预约 AI 业务操作员产品演示。",
    },
  },
  privacy: {
    en: {
      title: "ClawEase | Privacy Policy",
      description: "Read how MytePro Technology Limited handles information submitted through the ClawEase website, demos, and contact forms.",
    },
    zh: {
      title: "ClawEase | 隐私政策",
      description: "了解 MytePro Technology Limited 如何处理 ClawEase 官网、演示流程和联系表单中的相关信息。",
    },
  },
  cookie: {
    en: {
      title: "ClawEase | Cookie Policy",
      description: "Read how ClawEase uses cookies, pixels, tags, and similar technologies for website operations, analytics, and advertising measurement.",
    },
    zh: {
      title: "ClawEase | Cookie 政策",
      description: "了解 ClawEase 如何使用 Cookie、像素、标签及类似技术进行网站运行、统计分析和广告效果衡量。",
    },
  },
  terms: {
    en: {
      title: "ClawEase | Terms of Use",
      description: "Review the terms for visiting the ClawEase website, requesting demos, and using public demo experiences.",
    },
    zh: {
      title: "ClawEase | 使用条款",
      description: "查看访问 ClawEase 官网、预约演示和使用公开演示体验时适用的使用条款。",
    },
  },
  about: {
    en: {
      title: "ClawEase | About | MytePro Technology Limited",
      description: "Learn about ClawEase, a MytePro Technology Limited product brand providing AI business operator and customer request automation software for SMB service teams.",
    },
    zh: {
      title: "ClawEase | 关于我们 | MytePro Technology Limited",
      description: "了解 ClawEase：由 MytePro Technology Limited 运营，面向 SMB 服务团队的 AI 业务操作员和客户请求自动化软件产品品牌。",
    },
  },
  download: {
    en: {
      title: "ClawEase | Download | Select Your Industry and Platform",
      description: "Select your industry and platform to download the current ClawEase production release.",
    },
    zh: {
      title: "ClawEase | 下载 | 选择行业与系统版本",
      description: "选择你的行业和系统平台，下载当前正式发布的 ClawEase 客户端。",
    },
  },
  demo: {
    en: {
      title: "ClawEase | Free AI Receptionist & Dental Workflow Demo",
      description: "Try a free customer service and free receptionist experience with a free business workflow for dental booking across text, web form, and voice.",
    },
    zh: {
      title: "ClawEase | 牙科预约在线演示",
      description: "体验 ClawEase 通过文字、网页表单和语音完成牙科预约的交互流程。",
    },
  },
  "ws-inspector": {
    en: {
      title: "ClawEase | Demo Diagnostics",
      description: "Internal diagnostics for the ClawEase demo connection.",
    },
    zh: {
      title: "ClawEase | 演示诊断",
      description: "ClawEase 演示连接的内部诊断页面。",
    },
  },
  "not-found": {
    en: { title: "ClawEase | Page Not Found", description: "The page you are looking for does not exist." },
    zh: { title: "ClawEase | 页面不存在", description: "你访问的页面不存在。" },
  },
};

function canonicalPath(page: SeoPage, lang: Lang) {
  const root = lang === "zh" ? "/cn/ai-receptionist" : "/ai-receptionist";
  if (page === "home") return lang === "en" ? "/" : root;
  if (page === "contact") return `${root}/contact`;
  if (page === "privacy") return lang === "zh" ? `${root}/privacy` : `${root}/privacy-policy`;
  if (page === "cookie") return `${root}/cookie-policy`;
  if (page === "terms") return `${root}/terms-of-use`;
  if (page === "about") return `${root}/about`;
  if (page === "download") return `${root}/download`;
  if (page === "demo") return `${root}/demo/dental-booking`;
  if (page === "ws-inspector") return `${root}/demo/ws-inspector`;
  return null;
}

function pairedAlternates(page: SeoPage) {
  const enPath = canonicalPath(page, "en");
  const zhPath = canonicalPath(page, "zh");
  if (!enPath || !zhPath || page === "ws-inspector") return [];

  return [
    { hreflang: "en", href: `${siteOrigin}${enPath}` },
    { hreflang: "zh-CN", href: `${siteOrigin}${zhPath}` },
    ...(page === "home" ? [{ hreflang: "zh-HK", href: `${siteOrigin}/hk/ai-receptionist` }] : []),
    ...(page === "contact" ? [{ hreflang: "zh-HK", href: `${siteOrigin}/hk/ai-receptionist/contact` }] : []),
    { hreflang: "x-default", href: `${siteOrigin}${enPath}` },
  ];
}

function pageKeywords(page: SeoPage, lang: Lang) {
  const base =
    lang === "zh"
      ? ["ClawEase", "AI 客服", "AI 前台", "AI 业务操作员", "业务流程自动化"]
      : ["ClawEase", "AI customer service", "AI receptionist", "AI business operator", "business workflow automation"];

  if (page === "home" || page === "contact" || page === "demo") {
    return lang === "zh"
      ? ["免费 AI 客服", "免费 AI 前台", "免费业务流程", "免费 AI 演示", ...base]
      : ["free customer service", "free receptionist", "free business workflow", "free AI demo", ...base];
  }

  return base;
}

function structuredData(page: SeoPage, lang: Lang, canonicalUrl: string | null, description: string, keywords: string[]) {
  if (!canonicalUrl) return null;

  if (page === "home") {
    return {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "ClawEase",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web, Windows, macOS",
      url: canonicalUrl,
      description,
      keywords: keywords.join(", "),
      publisher: {
        "@type": "Organization",
        name: "MytePro Technology Limited",
        alternateName: "ClawEase",
        url: siteOrigin,
      },
    };
  }

  if (page === "about") {
    return {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "MytePro Technology Limited",
      alternateName: "ClawEase",
      url: canonicalUrl,
      description,
    };
  }

  if (page === "contact" || page === "privacy") {
    return {
      "@context": "https://schema.org",
      "@type": page === "contact" ? "ContactPage" : "WebPage",
      name: seoCopy[page][lang].title,
      url: canonicalUrl,
      description,
    };
  }

  return null;
}

export function getSeoDocument(page: SeoPage, lang: Lang): SeoDocument {
  const copy = seoCopy[page][lang];
  const path = canonicalPath(page, lang);
  const canonicalUrl = path ? `${siteOrigin}${path}` : null;
  const keywords = pageKeywords(page, lang);
  const robots: RobotsValue =
    page === "not-found" || page === "ws-inspector"
      ? "noindex,nofollow"
      : page === "privacy" || page === "cookie" || page === "terms" || page === "download" || page === "demo"
        ? "noindex,follow"
        : "index,follow";

  return {
    ...copy,
    keywords,
    robots,
    canonicalUrl,
    alternates: page === "not-found" ? [] : pairedAlternates(page),
    structuredData: structuredData(page, lang, canonicalUrl, copy.description, keywords),
  };
}

export function getHongKongHomeSeoDocument(): SeoDocument {
  const base = getSeoDocument("home", "zh");
  const canonicalUrl = `${siteOrigin}/hk/ai-receptionist`;
  const description = "ClawEase 為香港企業提供 AI 接待，支援廣東話、普通話、英語等多種語言，整合 WhatsApp、電話及電郵，接通 Google Calendar、PMS 及 CRM。";
  const keywords = ["ClawEase", "AI 接待員", "廣東話 AI 客服", "香港 AI 接待", "多語言接待", "免費 AI 接待"];
  return {
    ...base,
    title: "ClawEase | 香港多語言 AI 接待員 | 廣東話、普通話及英語",
    description,
    keywords,
    canonicalUrl,
    alternates: [
      { hreflang: "en-US", href: siteOrigin },
      { hreflang: "zh-HK", href: canonicalUrl },
      { hreflang: "x-default", href: siteOrigin },
    ],
    structuredData: { ...base.structuredData, url: canonicalUrl, description, keywords: keywords.join(", "), inLanguage: "zh-HK" },
  };
}

export function getHongKongContactSeoDocument(): SeoDocument {
  const base = getSeoDocument("contact", "zh");
  const canonicalUrl = `${siteOrigin}/hk/ai-receptionist/contact`;
  const title = "ClawEase | 聯絡我們 | 預約產品示範";
  const description = "聯絡 ClawEase 香港團隊，預約 AI 接待員產品示範，了解廣東話、普通話及英語多語言接待如何配合你的業務流程。";
  return {
    ...base,
    title,
    description,
    keywords: ["ClawEase", "香港 AI 接待員", "廣東話 AI 客服", "預約示範", "AI 接待"],
    canonicalUrl,
    alternates: [
      { hreflang: "en", href: `${siteOrigin}/ai-receptionist/contact` },
      { hreflang: "zh-CN", href: `${siteOrigin}/cn/ai-receptionist/contact` },
      { hreflang: "zh-HK", href: canonicalUrl },
      { hreflang: "x-default", href: `${siteOrigin}/ai-receptionist/contact` },
    ],
    structuredData: { ...base.structuredData, name: title, url: canonicalUrl, description, inLanguage: "zh-HK" },
  };
}
